import React, { useMemo, useState } from 'react';
import { Button, Drawer, Empty, Spin, Tooltip, message } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  CustomerServiceOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LockOutlined,
  MessageOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ConciergeBellIcon from '../../receptionist_ai/components/ConciergeBellIcon';
import { ContactFollowedBy } from '../../contacts/components/ContactTags';
import { SparkleIcon } from '../../contacts/components/ContactDrawer';
import { ConversationCallItem } from '../../../../domain/models/Conversation';
import { ConversationCallTranscriptResponse } from '../../../../domain/use_cases/conversations/getConversationCallTranscript';
import {
  CallTranscriptMessage,
  ConversationDisplay,
  formatCallClock,
  formatThreadDay,
  getCallTaskStatus,
  parseCallTranscript,
} from '../types/ConversationDisplay';
import useConversationCallRecordingController from '../controllers/useConversationCallRecordingController';
import useConversationCallTranscriptController from '../controllers/useConversationCallTranscriptController';
import CallDirectionIcon, { CALL_LABELS, getCallDirection } from './CallDirectionIcon';

const SOON_LABEL = 'Bientôt disponible';

const TASK_STATUS_LABELS = { done: 'Terminée', overdue: 'En retard' };

interface ConversationCallDrawerProps {
  conversation: ConversationDisplay;
  // null when closed
  item: ConversationCallItem | null;
  onClose: () => void;
  onShowDetails: () => void;
}

const formatDateTime = (timestamp: number) =>
  `${formatThreadDay(timestamp)} à ${dayjs(timestamp).format('HH:mm')}`;

const buildTranscriptMessages = (
  transcript: ConversationCallTranscriptResponse | null,
  conversation: ConversationDisplay
) =>
  transcript
    ? parseCallTranscript(transcript.transcription_srt, {
        linePhone: transcript.line_phone,
        contactName: conversation.contact ? conversation.fullName : null,
        contactPhone: conversation.phone,
      })
    : [];

const saveTranscript = (fileName: string, messages: CallTranscriptMessage[]) => {
  const text = messages
    .map((entry) =>
      [entry.time && `[${entry.time}]`, entry.speaker && `${entry.speaker} :`, entry.text]
        .filter(Boolean)
        .join(' ')
    )
    .join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const CallRecording: React.FC<{ subscriptionId: string; item: ConversationCallItem }> = ({
  subscriptionId,
  item,
}) => {
  const { state } = useConversationCallRecordingController(
    subscriptionId,
    item.id,
    item.has_recording
  );

  return (
    <div className="conversations__call-detail-recording">
      <div className="conversations__call-detail-card-head">
        <span className="conversations__call-detail-card-title">
          <CustomerServiceOutlined />
          Enregistrement de l'appel
        </span>
        {state.url && (
          // Signed URL on another domain: the download attribute is ignored, the audio opens in a new tab
          <Button
            size="small"
            icon={<DownloadOutlined />}
            href={state.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Télécharger audio
          </Button>
        )}
      </div>
      {state.isLoading ? (
        <Spin size="small" />
      ) : (
        <>
          {/* Un enregistrement d'appel n'a pas de piste de sous-titres à fournir. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            key={state.url}
            controls
            preload="metadata"
            src={state.url || undefined}
            className={classNames('conversations__call-detail-audio', {
              'conversations__call-detail-audio--disabled': !state.url,
            })}
          />
          {!state.url && (
            <p className="conversations__call-detail-hint">
              Enregistrement audio indisponible pour cet appel.
            </p>
          )}
        </>
      )}
    </div>
  );
};

const CallTranscriptView: React.FC<{
  messages: CallTranscriptMessage[];
  isLoading: boolean;
  hasFailed: boolean;
  onBack: () => void;
}> = ({ messages, isLoading, hasFailed, onBack }) => (
  <>
    <button type="button" className="conversations__call-detail-back" onClick={onBack}>
      <ArrowLeftOutlined />
      Retour au résumé
    </button>
    <div className="conversations__call-detail-card">
      <span className="conversations__call-detail-card-title">Transcription</span>
      {isLoading ? (
        <Spin />
      ) : hasFailed ? (
        <Empty description="Impossible de charger la transcription. Revenez au résumé et réessayez." />
      ) : messages.length === 0 ? (
        <Empty description="Aucune transcription disponible" />
      ) : (
        <div className="conversations__transcript">
          {messages.map((entry, index) => (
            <div
              key={index}
              className={classNames('conversations__transcript-bubble', {
                'conversations__transcript-bubble--user': entry.isUser,
              })}
            >
              {(entry.speaker || entry.time) && (
                <div className="conversations__transcript-head">
                  <span className="conversations__transcript-speaker">{entry.speaker}</span>
                  <span>{entry.time}</span>
                </div>
              )}
              <div>{entry.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  </>
);

// Body of the drawer, keyed by call: the transcript closes when another call is opened
const CallDetail: React.FC<{ conversation: ConversationDisplay; item: ConversationCallItem }> = ({
  conversation,
  item,
}) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { state, actions } = useConversationCallTranscriptController(
    conversation.subscriptionId,
    item.id,
    isTranscriptOpen
  );
  const messages = useMemo(
    () => buildTranscriptMessages(state.transcript, conversation),
    [state.transcript, conversation]
  );
  const direction = getCallDirection(item);

  const copySummary = () => {
    navigator.clipboard.writeText([item.title, item.summary_markdown].filter(Boolean).join('\n\n'));
    message.success('Résumé copié dans le presse-papier');
  };

  const downloadTranscript = async () => {
    setIsDownloading(true);
    try {
      const downloaded = buildTranscriptMessages(await actions.loadTranscript(), conversation);
      if (downloaded.length > 0) {
        saveTranscript(
          `transcription-${conversation.fullName}-${dayjs(item.timestamp).format(
            'YYYY-MM-DD-HHmm'
          )}.txt`,
          downloaded
        );
      } else {
        message.error('Aucune transcription disponible pour cet appel.');
      }
    } catch {
      message.error('Impossible de télécharger la transcription.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="conversations__call-detail">
      <div className="conversations__call-detail-meta">
        <span className="conversations__call-detail-line">
          Ligne
          <ContactFollowedBy label={conversation.followedBy} isOwn={conversation.isOwn} />
        </span>
        <span
          className={classNames('conversations__call-detail-kind', {
            'conversations__call--missed': direction === 'missed',
          })}
        >
          {item.is_receptionist ? (
            <ConciergeBellIcon />
          ) : (
            <CallDirectionIcon direction={direction} />
          )}
          <span className="conversations__call-label">
            {item.is_receptionist ? 'Réceptionniste Donna' : CALL_LABELS[direction]}
          </span>
          <span>
            {formatDateTime(item.timestamp)}
            {item.duration > 0 && ` · ${formatCallClock(item.duration)}`}
          </span>
        </span>
      </div>

      {conversation.contact?.isConfidential ? (
        <section className="contacts__summary contacts__summary--confidential">
          <LockOutlined className="contacts__confidential-icon" />
          <div className="contacts__confidential-content">
            <div className="contacts__summary-title">Contact confidentiel</div>
            <p className="contacts__muted">
              Cet appel n'a pas été enregistré et aucune analyse IA n'est générée pour ce contact.
            </p>
          </div>
        </section>
      ) : isTranscriptOpen ? (
        <CallTranscriptView
          messages={messages}
          isLoading={state.isLoading}
          hasFailed={state.hasFailed}
          onBack={() => setIsTranscriptOpen(false)}
        />
      ) : (
        <>
          <div className="conversations__call-detail-card">
            <div className="conversations__call-detail-card-head">
              <span className="conversations__call-detail-card-title">
                <SparkleIcon />
                Résumé de l'appel
              </span>
              {(item.title || item.summary_markdown) && (
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  iconPosition="end"
                  onClick={copySummary}
                >
                  Copier résumé
                </Button>
              )}
            </div>
            {item.title && <div className="conversations__call-detail-title">{item.title}</div>}
            {item.summary_markdown ? (
              <div className="conversations__call-detail-summary">
                <ReactMarkdown>{item.summary_markdown}</ReactMarkdown>
              </div>
            ) : (
              <p className="conversations__call-detail-empty">
                Aucun résumé disponible pour cet appel.
              </p>
            )}
            {item.has_transcript && (
              <div className="conversations__call-detail-actions">
                <Button
                  className="conversations__call-detail-transcript-button"
                  icon={<FileTextOutlined />}
                  onClick={() => setIsTranscriptOpen(true)}
                >
                  Voir transcription
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  loading={isDownloading}
                  onClick={downloadTranscript}
                >
                  Télécharger transcription
                </Button>
              </div>
            )}
          </div>

          <div className="conversations__call-detail-card">
            <div className="conversations__call-detail-card-head">
              <span className="conversations__call-detail-card-title">Tâches détectées</span>
              <span className="conversations__call-detail-count">{item.tasks.length}</span>
            </div>
            {item.tasks.length === 0 ? (
              <p className="conversations__call-detail-empty">
                Aucune tâche détectée pour cet appel.
              </p>
            ) : (
              item.tasks.map((task) => {
                const status = getCallTaskStatus(task);
                return (
                  <div
                    key={task.id}
                    className={classNames('conversations__call-detail-task', {
                      'conversations__call-detail-task--done': status === 'done',
                    })}
                  >
                    <span className="conversations__call-detail-task-icon">
                      {status === 'done' ? <CheckOutlined /> : <FileTextOutlined />}
                    </span>
                    <div className="conversations__call-detail-task-body">
                      <div className="conversations__call-detail-task-title">{task.title}</div>
                      {task.description && (
                        <div className="conversations__call-detail-task-text">
                          {task.description}
                        </div>
                      )}
                      <div className="conversations__call-detail-hint">
                        {formatDateTime(task.timestamp)}
                      </div>
                    </div>
                    {status && (
                      <span
                        className={`conversations__call-detail-status conversations__call-detail-status--${status}`}
                      >
                        {TASK_STATUS_LABELS[status]}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ConversationCallDrawer: React.FC<ConversationCallDrawerProps> = ({
  conversation,
  item,
  onClose,
  onShowDetails,
}) => {
  const isConfidential = !!conversation.contact?.isConfidential;

  return (
    <Drawer
      open={!!item}
      onClose={onClose}
      width={640}
      closable={false}
      className="contacts__drawer"
      footer={
        item &&
        !isConfidential && (
          <CallRecording key={item.id} subscriptionId={conversation.subscriptionId} item={item} />
        )
      }
      title={
        <div className="contacts__drawer-header conversations__call-detail-header">
          <span className="contacts__avatar contacts__avatar--large">{conversation.initials}</span>
          <div className="contacts__drawer-identity">
            <div className="contacts__drawer-name">{conversation.fullName}</div>
            {isConfidential && (
              <span className="contacts__confidential-badge">
                <LockOutlined />
                Contact confidentiel · non enregistré
              </span>
            )}
            <div className="contacts__drawer-owner">{conversation.formattedPhone}</div>
            {conversation.contact && (
              <button
                type="button"
                className="conversations__call-detail-link"
                onClick={onShowDetails}
              >
                <InfoCircleOutlined />
                Fiche contact
              </button>
            )}
          </div>
        </div>
      }
      extra={
        <div className="contacts__drawer-actions">
          <Tooltip title={`Appeler · ${SOON_LABEL}`}>
            <Button icon={<PhoneOutlined />} disabled aria-label="Appeler" />
          </Tooltip>
          {/* The composer of the thread is right behind the drawer */}
          <Tooltip title="Message">
            <Button icon={<MessageOutlined />} onClick={onClose} aria-label="Message" />
          </Tooltip>
          <Button shape="circle" icon={<CloseOutlined />} onClick={onClose} aria-label="Fermer" />
        </div>
      }
    >
      {item && <CallDetail key={item.id} conversation={conversation} item={item} />}
    </Drawer>
  );
};

export default ConversationCallDrawer;
